import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { OfferType } from '@occc-app/offerValidationService/types';

import { useChainId } from '@occc-app/Chain/hooks';
import { Offer_Engagement_Type } from '@occc/graphql';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { useMakeOffersFilter } from '@occc-app/offerHelpers/hooks/use-make-offers-filter.hook';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';
import { OfferCreateConfigurePage } from '../OfferCreatePage/OfferCreateConfigurePage';
import { routeCreator } from '../utils';
import { getOfferConfigureFormInitialValues } from '../components/OfferConfigureForm/utils/configureFormInitialValues';
import { OfferConfigureFormValues, OfferConfigureFormInitialValues } from '../types';
import { getConfigureFormOperations } from '../components/OfferConfigureForm/utils/configureFormOperations';
import { ConfigureForm } from '../components/OfferConfigureForm/ConfigureForm';
import { messages } from '../messages';

export const OfferCreatePageContainer = () => {
  const { id: campaignId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Offer'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMakeOffersFilter();

  return (
    <OfferCreateConfigurePage<OfferConfigureFormValues, OfferConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Offer}
      engagementType={Offer_Engagement_Type.Transactional}
      campaignId={campaignId}
      onOfferCreated={(id) =>
        navigate(routeCreator.configureWithTab(campaignId, id, CampaignComponentTabs.Target))
      }
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getOfferConfigureFormInitialValues}
      renderConfigureForm={({ loading, initialValues, onSubmit }) => (
        <ConfigureForm
          loading={loading}
          onSubmit={onSubmit}
          initialValues={initialValues}
          onCancelClick={navigateToCampaign}
          makeOffersFilter={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
